import { Sparkles, Menu, Calendar, CalendarClock, PersonStanding, LucideProps, KeyRound, MessageCircle } from "lucide-react";
import { Button as BaseButton } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import React, { MutableRefObject } from "react";
import HeroBackground from "@/components/HeroBackground";
import { useUserContext } from "@/contexts/user";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useNavigationContext } from "@/contexts/navigation";
import { cn } from "@/lib/utils";
import { motion } from "motion/react";
import { useTask } from "@/contexts/appContent/appContentHooks";
import { Dialog } from "@/components/Dialog";
import { TaskMarkdown } from "@/components/TaskMarkdown";
import { ChatDialog } from "@/components/chat/chat-dialog";

type HeroSectionProps = {
    continueJourneyRef: React.MutableRefObject<HTMLElement>;
    dailyRef: React.MutableRefObject<HTMLElement>;
};

const CHECKIN_QUESTION_COUNT = 3;

export default function HeroSection({ continueJourneyRef, dailyRef }: HeroSectionProps) {
    const { profile, logout } = useUserContext();

    return (
        <section className="min-h-screen relative overflow-hidden flex">
            <HeroBackground />

            <div className="absolute flex flex-row gap-4 top-[1rem] left-[1rem]">
                {/* <NavigationMenuDropdown /> */}
                {profile &&
                    <ChatDialog
                        trigger={
                            <MessageCircle
                                color="white"
                                fill="white"
                                size={36}
                            />
                        }
                        userId={profile.user_id}
                        userName={profile.first_name}
                    />
                }
            </div>

            {/* Logout Button */}
            <div className="absolute top-[1rem] right-[1rem]">
                {profile && (
                    <BaseButton
                        variant="default"
                        onClick={logout}
                        className="text-sm"
                    >
                        Logga ut
                    </BaseButton>
                )}
            </div>


            <div className="relative container w-full px-6 pb-20 mt-14 text-center">
                <div className="h-full max-w-4xl w-full mx-auto flex flex-col justify-between items-center">
                    {/* <h1 className="mx-auto w-fit font-bold font-['adobe-garamond-pro'] text-5xl md:text-7xl mb-6 tracking-tight text-[#eae5d3] drop-shadow-lg">
                        Konsten <span className="text-[#988a8c]">att göra</span>
                        <span className="block drop-shadow-lg">det möjliga möjligt</span>
                        <div className="w-full text-right font-normal italic text-3xl md:text-5xl">En Livsstil</div>
                    </h1> */}

                    <div className="w-full flex flex-col items-center gap-4">

                        <motion.img
                            className="w-96 pt-8"
                            src="/img/hopeifier_the_habit_changer.svg"
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 1, ease: "easeOut" }}
                        />

                        <motion.img
                        className="w-64"
                        src="/img/dare_to_live.svg"
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, ease: "easeOut" }}
                        />
                    </div>

                    <motion.img
                        src="/img/hopeifier-mascot-green-DMDJ38AU.png" className="size-48"
                        initial={{ opacity: 0, scale: 0.5 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.8, delay: 0.6, ease: "easeOut" }}
                    />

                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, delay: 0.8, ease: "easeOut" }}
                        className={cn(
                            "grid grid-cols-2 gap-6 justify-center place-items-center",
                            profile !== null && "md:grid-cols-4",
                        )}
                    >

                        {profile === null ?
                            <>
                                <RegisterButton />
                                <LoginButton />
                            </>
                            :
                            <>
                                <BookingButton />
                                <DailyButton scrollTargetRef={dailyRef} />
                                <MasterKeyButton />
                                <ContinueJourneyButton scrollTargetRef={continueJourneyRef} />
                            </>
                        }
                    </motion.div>
                    {/* used for spacing */}
                    <div />
                </div>
            </div>
        </section >
    );
}

type ButtonProps = {
    icon?: React.ComponentType<LucideProps>;
    text?: string;
    subtext?: string;
    onClick?: () => void;
    iconProps?: LucideProps;
    disabled?: boolean;
};
function Button({ icon: Icon, text, subtext, iconProps, onClick }: ButtonProps) {
    return <BaseButton
        size="lg"
        variant="default"
        className="text-lg px-4 py-8 group transition-all duration-300 hover:scale-105"
        onClick={onClick}
    >
        {Icon && <Icon
            {...iconProps}
            className={cn(
                "mr-2 size-8 group-hover:rotate-12 transition-transform",
                iconProps?.className
            )}
        />}
        <div className="flex text-left flex-col grow">
            {text}
            {subtext && <span className="text-sm italic font-normal">
                {subtext}
            </span>}
        </div>
    </BaseButton>;
}

// Used as a Dialog trigger (see MasterKeyButton), and Dialog renders its trigger
// through Radix's asChild/Slot — which clones this and hands it a ref plus the
// aria/data attributes that drive the dialog. All of it has to reach the real
// <button>, so this forwards refs and spreads what it doesn't consume itself.
type ButtonRoundProps = ButtonProps & Omit<React.ComponentPropsWithoutRef<"button">, keyof ButtonProps>;

const ButtonRound = React.forwardRef<HTMLButtonElement, ButtonRoundProps>(function ButtonRound(
    { icon: Icon, text, subtext, iconProps, onClick, disabled, ...props },
    ref
) {
    return (
        <button
            {...props}
            ref={ref}
            className="relative rounded-full size-40 bg-blue-300 flex flex-col items-center justify-center"
            onClick={onClick}
            disabled={disabled}
        >
            {Icon && <Icon
                {...iconProps}
                className={cn(
                    "mr-2 size-8 group-hover:rotate-12 transition-transform text-foreground-dark",
                    iconProps?.className
                )}
            />}
            {disabled && <div className="absolute rounded-full size-40 bg-black/30" />}
            <span className="font-bold text-foreground-dark">{text}</span>
            <span className="text-[10px] text-foreground-dark px-3">{subtext}</span>
        </button>
    );
});

function RegisterButton() {
    const navigate = useNavigate();
    return <ButtonRound
        onClick={() => navigate("/register")}
        icon={PersonStanding}
        text="Börja din resa"
        subtext="Registrera dig"
        iconProps={{
            className: "scale-150"
        }}
    />;
}

function LoginButton() {
    const navigate = useNavigate();
    return <ButtonRound
        onClick={() => navigate("/login")}
        icon={Sparkles}
        text="Fortsätt din resa"
        subtext="Logga in"
    />;
}

function BookingButton() {
    const { bookingSectionRef } = useNavigationContext();
    return <ButtonRound
        icon={Calendar}
        text="Boka"
        subtext="Boka klasser, Test-talks och events."
        onClick={() => bookingSectionRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
    />;
}

function DailyButton({ scrollTargetRef }: { scrollTargetRef: MutableRefObject<HTMLElement> }) {
    return <ButtonRound
        icon={CalendarClock}
        text="Daily"
        subtext="Dagens dare, skatta din balans och övningar."
        onClick={() => scrollTargetRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
    />;
}

function ContinueJourneyButton({ scrollTargetRef }: { scrollTargetRef: MutableRefObject<HTMLElement> }) {
    return <ButtonRound
        icon={Sparkles}
        text="Fortsätt din resa"
        subtext="Där du senast slutade, utan att börja om."
        onClick={() => scrollTargetRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
    />;
}

function MasterKeyButton() {
    const { completed, data } = useTask({ taskId: 185 });

    return (
        <Dialog
            titleClass="text-step-2"
            trigger={
                <ButtonRound
                    disabled={!completed}
                    icon={KeyRound}
                    text="Din master key"
                    subtext="Din inre riktning, grundad i balans."
                />
            }
            title="Din master key"
        >
            <TaskMarkdown textColor="text-step-2">
                {`
<br>

# Värderingar
${data?.data?.["md-1"]}
<br><br>

# Syfte
${data?.data?.["md-2"]}
<br><br>

# Nordstjärna
${data?.data?.["md-3"]}
`}
            </TaskMarkdown>
        </Dialog>);
}

function NavigationMenuDropdown() {
    const { balanceSectionRef, bookingSectionRef, journeySectionRef } = useNavigationContext();
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Menu color="white" />
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56">
                {/* <NavigationMenuDropdownItem scrollDestinationRef={balanceSectionRef} text="Din balans" />
                <NavigationMenuDropdownItem scrollDestinationRef={journeySectionRef} text="Din resa" /> */}
                <NavigationMenuDropdownItem scrollDestinationRef={bookingSectionRef} text="Boka" />
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

function NavigationMenuDropdownItem({ scrollDestinationRef, text }) {
    return (
        <>
            {scrollDestinationRef.current && <DropdownMenuItem onSelect={() => scrollDestinationRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })}>{text}</DropdownMenuItem>}
        </>
    );
}

